import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Table, Card, Tag, Space, Button, message, Popconfirm, Modal, Form, Input } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { getApps, createApp, updateApp, deleteApp, type App } from '../api/admin'

const Apps = () => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingApp, setEditingApp] = useState<App | null>(null)
  const [form] = Form.useForm()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['apps', page, pageSize],
    queryFn: () => getApps(page, pageSize).then((res) => res.data.data),
  })

  const handleCreate = () => {
    setEditingApp(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  const handleEdit = (app: App) => {
    setEditingApp(app)
    form.setFieldsValue({
      appId: app.app_id,
      name: app.name,
      description: app.description,
    })
    setIsModalOpen(true)
  }

  const handleDelete = async (appId: string) => {
    try {
      await deleteApp(appId)
      message.success('应用已删除')
      refetch()
    } catch (error) {
      message.error('删除失败')
    }
  }

  const handleSubmit = async (values: any) => {
    try {
      if (editingApp) {
        await updateApp(editingApp.app_id, {
          name: values.name,
          description: values.description,
        })
        message.success('应用更新成功')
      } else {
        await createApp({
          appId: values.appId,
          name: values.name,
          description: values.description,
        })
        message.success('应用创建成功')
      }
      setIsModalOpen(false)
      refetch()
    } catch (error: any) {
      message.error(error.response?.data?.error || '操作失败')
    }
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '应用ID',
      dataIndex: 'app_id',
    },
    {
      title: '名称',
      dataIndex: 'name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '活跃' : '已停用'}
        </Tag>
      ),
    },
    {
      title: '用户数',
      dataIndex: 'user_count',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: App) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除该应用吗？此操作不可恢复。"
            onConfirm={() => handleDelete(record.app_id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>应用管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建应用
        </Button>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps || 20)
            },
          }}
        />
      </Card>

      <Modal
        title={editingApp ? '编辑应用' : '新建应用'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsModalOpen(false)}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="appId"
            label="应用ID"
            rules={[
              { required: true, message: '请输入应用ID' },
              { pattern: /^[a-z0-9-]+$/, message: '只能包含小写字母、数字和连字符' },
            ]}
          >
            <Input disabled={!!editingApp} placeholder="例如: kaying-ai" />
          </Form.Item>
          <Form.Item
            name="name"
            label="应用名称"
            rules={[{ required: true, message: '请输入应用名称' }]}
          >
            <Input placeholder="例如: 咖影AI" />
          </Form.Item>
          <Form.Item
            name="description"
            label="应用描述"
          >
            <Input.TextArea rows={3} placeholder="应用描述（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Apps
